import { useEffect } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { RadioGroup } from '@/components/ui/RadioGroup';
import { SettingRow } from '@/components/ui/Field';
import { Switch } from '@/components/ui/Switch';
import { useSettingsPatch, useUserPrefs } from '@/api/bi/settings';
import { useTheme } from '@/auth/ThemeProvider';
import { storage, type UserPrefs } from '@/lib/storage';
import { SavingIndicator } from './SavingIndicator';

/** Theme, row density and motion — everything that changes how the app looks. */
export function AppearancePage() {
  const { theme, setTheme } = useTheme();
  const prefs = useUserPrefs();
  const patchSettings = useSettingsPatch();

  function patch(next: Partial<UserPrefs>) {
    // Mirror into the local cache first so cold-boot readers (main.tsx and the
    // filter store) see the new value, then send the merged object.
    storage.setPrefs(next);
    patchSettings({ prefs: storage.getPrefs() });
  }

  const density = prefs.density ?? 'comfortable';

  // Both of these are applied live rather than on next reload — you should be
  // able to see what you just chose.
  useEffect(() => {
    document.documentElement.dataset.animations = prefs.animations ? 'on' : 'off';
  }, [prefs.animations]);

  useEffect(() => {
    document.documentElement.dataset.density = density;
  }, [density]);

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow="Account"
        title="Appearance"
        description="How the app looks on this account. Saved automatically and applied everywhere you sign in."
        actions={<SavingIndicator />}
      />

      <Card>
        <CardHeader>
          <CardTitle>Theme</CardTitle>
        </CardHeader>
        <CardBody>
          <RadioGroup
            ariaLabel="Theme"
            value={theme}
            onChange={(next) => {
              setTheme(next);
              patchSettings({ theme: next });
            }}
            options={[
              { value: 'dark', label: 'Dark', hint: 'The default. Built for night-time operations rooms.' },
              { value: 'light', label: 'Light', hint: 'Better in a bright office or on a projector.' },
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Density</CardTitle>
        </CardHeader>
        <CardBody>
          <RadioGroup
            ariaLabel="Row density"
            value={density}
            onChange={(next) => patch({ density: next })}
            options={[
              {
                value: 'comfortable',
                label: 'Comfortable',
                hint: 'Roomier rows. Easier to read one record at a time.',
              },
              {
                value: 'compact',
                label: 'Compact',
                hint: 'Tighter rows so more of a queue fits on screen. Applies across every table.',
              },
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Motion</CardTitle>
        </CardHeader>
        <CardBody>
          <SettingRow
            label="Tasteful animations"
            hint="Turn off to flatten every transition — worth doing on slow remote sessions."
          >
            <Switch
              checked={prefs.animations}
              onCheckedChange={(v) => patch({ animations: v })}
              aria-label="Tasteful animations"
            />
          </SettingRow>
        </CardBody>
      </Card>
    </div>
  );
}
